import { get } from '../client/axiosClient';
import { reportEndpoints } from '../endpoints/report.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { PaginatedResponse } from '../types';
import type {
  EmployeeActivityReportRequest, EmployeeActivityReportRow, EmployeeActivitySummary,
  WipReportRequest, WipReportRow, WipReportSummary,
  TimesheetStatusReportRequest, TimesheetStatusReportRow,
} from '../types/report.types';

function toPaged<T>(raw: any): PaginatedResponse<T> {
  const pagedResult = raw?.data ?? {};
  return {
    success: raw?.success ?? false,
    message: raw?.message,
    data: pagedResult.items ?? [],
    pagination: {
      currentPage: pagedResult.page ?? 1,
      pageSize: pagedResult.pageSize ?? 20,
      totalPages: pagedResult.totalPages ?? 0,
      totalItems: pagedResult.totalCount ?? 0,
      hasNextPage: pagedResult.hasNext ?? false,
      hasPreviousPage: pagedResult.hasPrevious ?? false,
    },
  };
}

class ReportService {
  async getEmployeeActivity(params?: EmployeeActivityReportRequest): Promise<PaginatedResponse<EmployeeActivityReportRow>> {
    const endpoint = params
      ? buildEndpointWithQuery(reportEndpoints.employeeActivityPath(), params as Record<string, any>)
      : reportEndpoints.employeeActivity();
    const response = await get<any>(endpoint);
    return toPaged<EmployeeActivityReportRow>(response.data);
  }

  async getEmployeeActivitySummary(params?: EmployeeActivityReportRequest): Promise<EmployeeActivitySummary> {
    const endpoint = params
      ? buildEndpointWithQuery(reportEndpoints.employeeActivitySummaryPath(), params as Record<string, any>)
      : reportEndpoints.employeeActivitySummary();
    const response = await get<any>(endpoint);
    return response.data?.data ?? {
      totalMinutes: 0, totalHours: 0, totalStandardAmount: 0,
      totalDiscountedAmount: 0, totalPremiumAmount: 0, entryCount: 0,
    };
  }

  async getWip(params?: WipReportRequest): Promise<PaginatedResponse<WipReportRow>> {
    const endpoint = params
      ? buildEndpointWithQuery(reportEndpoints.wipPath(), params as Record<string, any>)
      : reportEndpoints.wip();
    const response = await get<any>(endpoint);
    return toPaged<WipReportRow>(response.data);
  }

  async getWipSummary(params?: WipReportRequest): Promise<WipReportSummary> {
    const endpoint = params
      ? buildEndpointWithQuery(reportEndpoints.wipSummaryPath(), params as Record<string, any>)
      : reportEndpoints.wipSummary();
    const response = await get<any>(endpoint);
    return response.data?.data ?? {
      engagementCount: 0, totalBudget: 0, totalWip: 0,
      totalInvoiced: 0, totalPaid: 0, totalOutstanding: 0,
    };
  }

  async getTimesheetStatus(params?: TimesheetStatusReportRequest): Promise<PaginatedResponse<TimesheetStatusReportRow>> {
    const endpoint = params
      ? buildEndpointWithQuery(reportEndpoints.timesheetStatusPath(), params as Record<string, any>)
      : reportEndpoints.timesheetStatus();
    const response = await get<any>(endpoint);
    return toPaged<TimesheetStatusReportRow>(response.data);
  }
}

export const reportService = new ReportService();
export default reportService;
